import { useReisestatistik } from '../../hooks/queries'
import type { Reisestatistik } from '../../api/types'
import { datumText } from '../../lib/ortszeit'
import { fehlertext } from '../../api/client'
import { useTexte } from '../../texte'

/**
 * The numbers of a trip (#78, 1.11.0): the route, the report, the weather --
 * a category on the trip page for the traveller and the administration
 * (1.12.0; a reader has no such category, and the server refuses them).
 *
 * **The server decides what comes** (`TripStats`): an admin reading along
 * gets the route up to their cutoff, the posts through `Visibility`, and no
 * count of replies at all -- that number would tell how many are in the
 * circle. This card shows what comes and leaves out what is `null`; it does
 * not know the rule.
 *
 * **The same page stands in the book** (`StatsView`), with the same rows in
 * the same order. Two places, one source: both read `TripStats`.
 */
export function StatistikCard({ tripId, isSender }: { tripId: number; isSender: boolean }) {
  const texte = useTexte()
  const t = texte.statistik
  const tk = texte.tageskarte
  const stats = useReisestatistik(tripId, tripId > 0)

  if (stats.isPending) {
    return (
      <div className="card card--seite" data-statistik="laedt">
        <h2 className="card__titel">{t.titel}</h2>
        <p className="card__lead">{t.laedt}</p>
      </div>
    )
  }
  if (stats.isError) {
    return (
      <div className="card card--seite" data-statistik="fehler">
        <h2 className="card__titel">{t.titel}</h2>
        <p className="notice" role="alert">{fehlertext(stats.error)}</p>
      </div>
    )
  }

  const s: Reisestatistik = stats.data
  const versatz = s.tz_offset_min
  const tag = (dayKey: string) => datumText(`${dayKey}T12:00:00Z`)
  const meter = (m: number) => tk.meter(Math.round(m))

  const wetterzeile = (w: NonNullable<Reisestatistik['weather']['warmest']>) =>
    t.gradAm(Math.round(w.temperature_c), datumText(w.observed_at, versatz), w.place)

  return (
    <>
      <div className="card card--seite" data-statistik="steht">
        <h2 className="card__titel">{t.titel}</h2>
        <p className="card__lead">{isSender ? t.lead : t.leadMitlesend}</p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">{t.route}</h2>
        {s.route === null ? (
          <p className="leise leise--eng" data-kennzahl="keine-route">{t.keineRoute}</p>
        ) : (
          <dl className="kennzahlen">
            <Kennzahl name="strecke" label={t.strecke} wert={tk.km(s.route.distance_m / 1000)} />
            <Kennzahl name="fahrzeit" label={t.fahrzeit} wert={tk.dauer(s.route.moving_s)} />
            <Kennzahl name="tage" label={t.tage} wert={String(s.route.days)} />
            <Kennzahl
              name="laengster-tag"
              label={t.laengsterTag}
              wert={t.tagMitStrecke(tag(s.route.longest.day), tk.km(s.route.longest.distance_m / 1000))}
            />
            {s.route.ascent_m !== null && (
              <Kennzahl
                name="hoehenmeter"
                label={t.hoehenmeter}
                wert={`${tk.bergauf(Math.round(s.route.ascent_m))}${tk.trenner}${tk.bergab(Math.round(s.route.descent_m ?? 0))}`}
              />
            )}
            {s.route.max_ele_m !== null && (
              <Kennzahl name="hoechster-punkt" label={t.hoechsterPunkt} wert={meter(s.route.max_ele_m)} />
            )}
            {s.route.min_ele_m !== null && (
              <Kennzahl name="niedrigster-punkt" label={t.niedrigsterPunkt} wert={meter(s.route.min_ele_m)} />
            )}
          </dl>
        )}
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">{t.bericht}</h2>
        <dl className="kennzahlen">
          <Kennzahl name="beitraege" label={t.beitraege} wert={String(s.posts.broadcasts)} />
          {s.posts.replies !== null && (
            <Kennzahl name="antworten" label={t.antworten} wert={String(s.posts.replies)} />
          )}
          <Kennzahl name="fotos" label={t.fotos} wert={String(s.posts.photos)} />
          <Kennzahl name="videos" label={t.videos} wert={String(s.posts.videos)} />
          <Kennzahl name="sprachnachrichten" label={t.sprachnachrichten} wert={String(s.posts.voices)} />
          <Kennzahl name="highlights" label={t.highlights} wert={String(s.posts.highlights)} />
        </dl>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">{t.wetter}</h2>
        {s.weather.warmest === null && s.weather.coldest === null ? (
          <p className="leise leise--eng" data-kennzahl="kein-wetter">{t.keinWetter}</p>
        ) : (
          <dl className="kennzahlen">
            {s.weather.warmest !== null && (
              <Kennzahl name="waermster" label={t.waermster} wert={wetterzeile(s.weather.warmest)} />
            )}
            {s.weather.coldest !== null && (
              <Kennzahl name="kaeltester" label={t.kaeltester} wert={wetterzeile(s.weather.coldest)} />
            )}
          </dl>
        )}
        {isSender && <p className="leise leise--eng">{t.imBuch}</p>}
      </div>
    </>
  )
}

/** One row: the name on the left, the number on the right. `data-kennzahl` is for the probe. */
function Kennzahl({ name, label, wert }: { name: string; label: string; wert: string }) {
  return (
    <div className="kennzahl" data-kennzahl={name} data-wert={wert}>
      <dt className="kennzahl__name">{label}</dt>
      <dd className="kennzahl__wert">{wert}</dd>
    </div>
  )
}
